import classNames from 'classnames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import {
  Fragment,
  FunctionComponent,
  KeyboardEventHandler,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Calendar, {
  CalendarProps,
  CalendarTileProperties,
} from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { dateService } from 'services/DateService';
import BLabeledInput from './BLabeledInput';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useHourGenerator } from 'hooks/useHourGenerator';
import { SearchHourOption } from 'types/search.type';
import { cn } from 'utils/cn';

export interface DateTimeOffset {
  hour: number;
  isOffset?: boolean;
  harborId?: number;
}

interface DisabledHour {
  disabled: boolean;
  isOffset?: boolean;
}

export type MappedReservedTimes = Record<string, DateTimeOffset[]>;

interface BDatePickerProps extends CalendarProps {
  children?: ReactNode;
  showTimePicker?: boolean;
  showTimeInInput?: boolean;
  disabledHours?: DateTimeOffset[];
  disabledDates?: MappedReservedTimes;
  inputClasses?: string;
  value: Date | null;
  label?: string;
  calendarClasses?: string;
  showOnLeft?: boolean;
  endHourOnNextDay?: number;
  disabledExceptDates?: string[];
  onChange: (val: Date) => void | Date;
  wrapperClass?: string;
  sameSizeInMobile?: boolean;
}

const BDatePicker: FunctionComponent<BDatePickerProps> = ({
  children,
  showTimePicker = false,
  showTimeInInput = false,
  showOnLeft = false,
  value,
  inputClasses,
  label,
  disabledHours = [],
  calendarClasses,
  disabledDates = {},
  endHourOnNextDay,
  disabledExceptDates,
  wrapperClass,
  sameSizeInMobile = false,
  onChange,
  ...props
}) => {
  const dayjs = dateService.getDayjs();
  const defaultValue = useMemo(() => dayjs(value ?? undefined), [value]);
  const hours = useHourGenerator(0, 24 + (endHourOnNextDay || 0));
  const hourListRef = useRef<HTMLUListElement>(null);

  const formatterForInput = useMemo(() => {
    return showTimePicker
      ? dateService.getFormattedDateTime
      : showTimeInInput
      ? dateService.getFormattedDateTime
      : dateService.getFormattedDate;
  }, [showTimePicker, showTimeInInput]);

  const { locale } = useRouter();
  const { t } = useTranslation('datepicker');
  const [showDatepicker, setShowDatepicker] = useState<boolean>(false);
  const [inputVal, setInputVal] = useState<string>(
    formatterForInput(defaultValue.toDate())
  );
  const [date, setDate] = useState<Date>(defaultValue.toDate());
  const [time, setTime] = useState<number>(
    showTimePicker ? defaultValue.hour() : 0
  );

  useEffect(() => {
    setInputVal(formatterForInput(defaultValue.toDate()));
    setDate(defaultValue.toDate());
    setTime(showTimePicker ? defaultValue.hour() : 0);
  }, [defaultValue, showTimePicker]);

  const preventTyping: KeyboardEventHandler<HTMLInputElement> = (event) => {
    event.preventDefault();
    return false;
  };

  const onChangeCallback = (val: Date) => {
    const newDate = onChange(val);
    setInputVal(formatterForInput(newDate ?? val));
  };

  const onCalendarChange = (val: Date) => {
    const timedVal = dayjs(val).hour(time).toDate();
    setDate(timedVal);
    onChangeCallback(timedVal);
  };

  const onTimePickerChange = (hour: SearchHourOption<number>) => {
    setTime(hour.value);
    const dateValue = dayjs(date)
      .add(hour.dayOffset, 'day')
      .hour(hour.value)
      .toDate();
    onChangeCallback(dateValue);
    if (hour.dayOffset > 0) {
      hourListRef.current?.scrollTo({
        behavior: 'smooth',
        top: 0,
      });
    }
  };

  const calculatedHours = useRef<Record<string, DisabledHour[]>>({});

  const checkIfAnHourDisabled = (
    currentDate: Date,
    hour: number
  ): DisabledHour => {
    const formattedDate = dateService.getFormattedDate(currentDate);
    const item =
      (disabledDates[formattedDate] &&
        disabledDates[formattedDate].find((item) => item.hour === hour)) ??
      disabledHours.find((item) => item.hour === hour);
    const isHourBeforeNow = currentDate.setHours(hour) < new Date().getTime();
    return {
      disabled:
        disabledHours.some((item) => item.hour === hour) ||
        !!item ||
        isHourBeforeNow,
      isOffset: item?.isOffset,
    };
  };

  const disableTiles = (props: CalendarTileProperties) => {
    // TODO: burda bi zamazingo lazims
    if (props.view !== 'month') {
      return dateService.isPastThenCurrentMonth(props.date);
    }

    if (disabledExceptDates?.length) {
      return !disabledExceptDates.includes(
        dateService.getFormattedDate(props.date)
      );
    }

    const date = dateService.getFormattedDate(props.date);
    const disabledHours = [...Array(24)].reduce(
      (acc, _, idx) => [...acc, checkIfAnHourDisabled(props.date, +idx)],
      []
    ) as DisabledHour[];
    calculatedHours.current[date] = disabledHours;

    const isAllHoursDisabled = disabledHours.every(
      (h: DisabledHour) => h.disabled === true
    );
    return (
      isAllHoursDisabled ||
      (disabledDates[date] && disabledDates[date].length === 24)
    );
  };

  useEffect(() => {
    const formattedDate = dateService.getFormattedDate(defaultValue.toDate());
    if (!calculatedHours.current[formattedDate]) {
      disableTiles({
        date: defaultValue.toDate(),
        activeStartDate: defaultValue.toDate(),
        view: 'month',
      });
    }
  }, [defaultValue]);

  const isHourDisabled = (hour: SearchHourOption<number>) => {
    const currentDate = dayjs(date).add(hour.dayOffset, 'day').toDate();
    const formattedDate = dateService.getFormattedDate(currentDate);
    const schedule = calculatedHours.current[formattedDate];
    if (!schedule) {
      disableTiles({
        date: currentDate,
        activeStartDate: currentDate,
        view: 'month',
      });
    }
    return calculatedHours.current[formattedDate][hour.value];
    // return checkIfAnHourDisabled(date, hour);
  };

  const hourListItem = (hour: SearchHourOption<number>) => {
    return (
      <li
        className={classNames(
          'flex cursor-pointer items-center gap-1 rounded-md py-1.5 pl-2 pr-4 hover:bg-slate-100',
          { '-selected-time': time === hour.value && hour.dayOffset === 0 },
          { '-disabled': isHourDisabled(hour).disabled },
          {
            'line-through':
              isHourDisabled(hour).disabled && !isHourDisabled(hour).isOffset,
          }
        )}
        role="presentation"
        onClick={() =>
          isHourDisabled(hour).disabled ? null : onTimePickerChange(hour)
        }
      >
        {hour.icon} {hour.label}
      </li>
    );
  };

  return (
    <div
      className={cn(
        'b-datepicker',
        { '-no-mobile': sameSizeInMobile },
        wrapperClass
      )}
    >
      <BLabeledInput
        label={label}
        value={inputVal}
        onKeyDown={preventTyping}
        readOnly
        className={classNames(inputClasses, 'cursor-pointer select-none')}
        onClick={() => setShowDatepicker(true)}
      />
      {!showDatepicker ? null : (
        <>
          <div
            className="fixed top-0 left-0 z-10 h-full w-full"
            onClick={() => setShowDatepicker(false)}
            role="presentation"
          />
          <div
            className={classNames('b-datepicker-wrapper', calendarClasses, {
              'right-0': showOnLeft,
              'left-0': !showOnLeft,
            })}
          >
            <div className="flex gap-2">
              <Calendar
                minDetail="year"
                value={date}
                onChange={onCalendarChange}
                tileDisabled={disableTiles}
                locale={locale}
                {...props}
              />
              {!showTimePicker ? null : (
                <div className="mx-1 border-l pl-2 pt-2">
                  <span className="mb-2 inline-block w-full text-center">
                    {t('hour')}
                  </span>
                  <ul
                    className="h-60 overflow-y-auto overflow-x-hidden md:h-[17rem]"
                    ref={hourListRef}
                  >
                    {hours.map((hour) => (
                      <Fragment key={`${hour.value}-${hour.dayOffset}`}>
                        {hour.value === 0 && hour.dayOffset > 0 ? (
                          <span className="my-1 ml-2 inline-block text-primary-500">
                            {t('night')}
                          </span>
                        ) : null}
                        {isHourDisabled(hour).isOffset ? (
                          <Tippy content={<span>{t('offsetTooltip')}</span>}>
                            {hourListItem(hour)}
                          </Tippy>
                        ) : (
                          hourListItem(hour)
                        )}
                      </Fragment>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {!children ? null : <div className="mt-2">{children}</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default BDatePicker;
